import {
  Add as AddIcon,
  Cable as PortIcon,
  Refresh as RefreshIcon,
  Router as ReaderIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import capitalize from "capitalize";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { PageShell, AdminSectionPanel } from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { useAuth } from "@/contexts/AuthContext";
import { CustomerService } from "@/services/features/CustomerService";
import { RFIDReaderService } from "@/services/features/RFIDReaderService";
import { StoreService } from "@/services/features/StoreService";
import { UserService } from "@/services/features/UserService";
import { formatInteger } from "@/utils/number";
import { formatStoreSelectorLabel, shouldUseLegacyCustomerStoreLabel } from "@/utils/store";

export interface RFIDReaderPort {
  port: number;
  changingRoomId: number;
  rssiFilter: number;
}

interface ChangingRoom {
  roomId: number;
  groupName: string;
  roomName: string;
}

interface ValidationErrors {
  [key: string]: boolean;
}

interface StoreOption {
  id: number;
  name: string;
  externalId: string;
  customerName?: string;
}

interface RFIDReaderFormProps {
  mode?: "create" | "edit";
}

const PORT_COUNT = 8;
const DEFAULT_RSSI_FILTER = -65;

export const createDefaultPorts = (): RFIDReaderPort[] =>
  Array.from({ length: PORT_COUNT }, (_, index) => ({
    port: index + 1,
    changingRoomId: 0,
    rssiFilter: DEFAULT_RSSI_FILTER,
  }));

export const createConfiguredPorts = (configuredPorts: RFIDReaderPort[]): RFIDReaderPort[] =>
  createDefaultPorts().map((defaultPort) => {
    const configuredPort = configuredPorts.find((port) => port.port === defaultPort.port);

    return configuredPort ? { ...defaultPort, ...configuredPort } : defaultPort;
  });

export const getConfiguredPorts = (ports: RFIDReaderPort[]): RFIDReaderPort[] =>
  ports.filter((port) => port.changingRoomId !== 0);

const RFIDReaderAdd: React.FC<RFIDReaderFormProps> = ({ mode = "create" }) => {
  const isEdit = mode === "edit";
  const { readerName } = useParams<{ readerName: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreOption | null>(null);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [storeName, setStoreName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [debug, setDebug] = useState(false);
  const [enableDeduplication, setEnableDeduplication] = useState(true);
  const [externalId, setExternalId] = useState("");
  const [ports, setPorts] = useState<RFIDReaderPort[]>(createDefaultPorts);
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [changingRooms, setChangingRooms] = useState<ChangingRoom[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoadingChangingRooms, setIsLoadingChangingRooms] = useState(false);

  const rfidReaderService = RFIDReaderService.getInstance();
  const storeService = StoreService.getInstance();
  const customerService = CustomerService.getInstance();
  const userService = UserService.getInstance();

  useEffect(() => {
    document.title = isEdit
      ? t("management.devices.rfid.readerEdit.documentTitle", { readerName })
      : t("management.devices.rfid.readerAdd.documentTitle");
  }, [isEdit, readerName, t]);

  useEffect(() => {
    if (isEdit) {
      void fetchReaderData();
      return;
    }

    void fetchInitialData();
  }, [isEdit, readerName]);

  useEffect(() => {
    if (selectedStore && !isEdit) {
      void fetchChangingRooms(selectedStore.id);
    } else {
      setChangingRooms([]);
    }
  }, [isEdit, selectedStore]);

  const fetchInitialData = async () => {
    setIsLoadingInitialData(true);
    try {
      const storeResponse = await storeService.getStores();
      const customerResponse = await customerService.getAllCustomers();
      const customers = customerResponse.data;

      if (!storeResponse.data || !customerResponse.success || !customers) {
        const errorMessage =
          customerResponse.error ||
          t("management.devices.rfid.readerAdd.notifications.failedFetchInitialData");
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: "error" });
        return;
      }

      const formattedStores = storeResponse.data.map((store) => {
        const customer = customers.find((candidate) => candidate.id === store.customerId);

        return {
          id: store.id,
          name: store.name,
          externalId: store.externalId,
          customerName: customer?.name,
        };
      });
      setStores(formattedStores);

      await generatePassword();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("management.devices.rfid.readerAdd.notifications.failedFetchInitialData");
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsLoadingInitialData(false);
    }
  };

  const fetchReaderData = async () => {
    if (!readerName) return;

    setIsLoadingInitialData(true);
    setError(null);

    try {
      const readerResponse = await rfidReaderService.getReaderByName(readerName);
      if (!readerResponse.success || !readerResponse.data) {
        throw new Error(
          readerResponse.error ||
            t("management.devices.rfid.readerDetails.errors.failedFetchReaderDetails"),
        );
      }

      const readerData = readerResponse.data;
      setStoreId(readerData.storeId);
      setName(readerData.name);
      setUsername(readerData.username || "");
      setPassword(readerData.password || "");
      setExternalId(readerData.externalId);
      setDebug(readerData.debug);
      setPorts(createConfiguredPorts(readerData.ports as RFIDReaderPort[]));
      setEnableDeduplication(readerData.enableDeduplication ?? true);

      const storeResponse = await storeService.getStoreById(readerData.storeId);
      if (!storeResponse.data) {
        throw new Error(t("management.devices.rfid.readerDetails.errors.failedFetchStoreDetails"));
      }
      setStoreName(storeResponse.data.name);

      await fetchChangingRooms(readerData.storeId, true);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("management.devices.rfid.readerDetails.errors.unexpected");
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsLoadingInitialData(false);
    }
  };

  const fetchChangingRooms = async (storeId: number, throwOnError = false) => {
    setIsLoadingChangingRooms(true);
    try {
      const response = await storeService.getChangingRoomsByStore(storeId);
      if (!response.success || !response.data) {
        throw new Error(t("management.devices.rfid.common.notifications.failedFetchChangingRooms"));
      }

      const formattedRooms: ChangingRoom[] = [];
      response.data.forEach((group) => {
        group.changingRooms.forEach((room) => {
          formattedRooms.push({
            roomId: room.id!,
            groupName: group.changingRoomGroup?.name
              ? capitalize.words(group.changingRoomGroup.name)
              : t("management.devices.rfid.common.empty.unknownGroup"),
            roomName: room.name
              ? capitalize.words(room.name)
              : t("management.devices.rfid.common.empty.unknownRoom"),
          });
        });
      });

      formattedRooms.sort(
        (a, b) => a.groupName.localeCompare(b.groupName) || a.roomName.localeCompare(b.roomName),
      );
      setChangingRooms(formattedRooms);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("management.devices.rfid.common.notifications.failedFetchChangingRooms");
      if (throwOnError) {
        throw new Error(errorMessage);
      }
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsLoadingChangingRooms(false);
    }
  };

  const generatePassword = async () => {
    try {
      const response = await userService.generatePassword();
      if (response.data) {
        setPassword(response.data);
      } else {
        enqueueSnackbar(
          t("management.devices.rfid.readerAdd.notifications.failedGeneratePassword"),
          {
            variant: "error",
          },
        );
      }
    } catch {
      enqueueSnackbar(t("management.devices.rfid.readerAdd.notifications.failedGeneratePassword"), {
        variant: "error",
      });
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const resolvedStoreId = isEdit ? storeId : selectedStore?.id;
    if (!resolvedStoreId) return;

    const errors: ValidationErrors = {};
    if (!name) errors.name = true;
    if (!username) errors.username = true;
    if (!password) errors.password = true;
    if (!externalId) errors.externalId = true;
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      const request = {
        storeId: resolvedStoreId,
        name,
        username,
        password,
        externalId,
        debug,
        ports: getConfiguredPorts(ports),
        enableDeduplication: enableDeduplication,
      };
      const response = isEdit
        ? await rfidReaderService.updateReader(request)
        : await rfidReaderService.createReader(request);

      if (response.success) {
        enqueueSnackbar(
          isEdit
            ? t("management.devices.rfid.readerEdit.dialogs.readerUpdatedMessage", { name })
            : t("management.devices.rfid.readerAdd.dialogs.readerAddedMessage", { name }),
          { variant: "success" },
        );
        void navigate("/rfid-readers");
      } else {
        if (isEdit) {
          throw new Error(
            response.error ||
              t("management.devices.rfid.readerEdit.notifications.failedUpdateReader"),
          );
        }

        enqueueSnackbar(t("management.devices.rfid.readerAdd.notifications.failedCreateReader"), {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(
        isEdit && error instanceof Error
          ? error.message
          : isEdit
            ? t("management.devices.rfid.readerEdit.notifications.failedUpdateReader")
            : t("management.devices.rfid.readerAdd.notifications.failedCreateReader"),
        { variant: "error" },
      );
    }
  };

  const handleStoreChange = (store: StoreOption | null) => {
    setSelectedStore(store);
    setPorts((previousPorts) => previousPorts.map((port) => ({ ...port, changingRoomId: 0 })));
    if (store) {
      const newName = `${store.id}`;
      setName(newName);
      setUsername(newName);
    }
  };

  const handlePortChange = (index: number, field: keyof RFIDReaderPort, value: string | number) => {
    setPorts((prevPorts) => {
      const newPorts = [...prevPorts];
      newPorts[index] = {
        ...newPorts[index],
        [field]: value,
      };
      return newPorts;
    });
  };

  return (
    <PageShell
      breadcrumbs="admin"
      title={
        isEdit
          ? t("management.devices.rfid.readerEdit.title")
          : t("management.devices.rfid.readerAdd.title")
      }
      titleAccessory={<LoadingRing loading={isLoadingInitialData || isLoadingChangingRooms} />}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box
        component="form"
        id="rfid-reader-add-form"
        onSubmit={(event) => void handleSubmit(event)}
      >
        <Stack spacing={3}>
          <AdminSectionPanel
            title={t("management.devices.rfid.readerDetails.sections.readerInformation")}
            icon={<ReaderIcon fontSize="small" />}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {isEdit ? (
                  <TextField
                    label={t("management.devices.devices.fields.store")}
                    value={storeId ? `${storeId} - ${storeName}` : ""}
                    disabled
                    fullWidth
                  />
                ) : (
                  <MuiAutocompleteMultiSelect
                    label={t("management.devices.devices.fields.store")}
                    options={stores}
                    value={selectedStore ? [selectedStore] : []}
                    onChange={(next) => handleStoreChange(next[0] ?? null)}
                    getOptionLabel={(option) =>
                      shouldUseLegacyCustomerStoreLabel(user?.role)
                        ? `${option.id}: ${option.customerName || t("management.devices.rfid.common.empty.unknownCustomer")} - ${option.name}`
                        : formatStoreSelectorLabel(option, t, user?.role, "name")
                    }
                    getOptionKey={(option) => option.id}
                    loading={isLoadingInitialData}
                    error={validationErrors.storeId}
                    helperText={
                      validationErrors.storeId
                        ? t("management.devices.rfid.common.helperText.selectStore")
                        : undefined
                    }
                    required
                  />
                )}
              </Grid>

              <Grid item xs={12} lg={6}>
                <TextField
                  label={t("management.devices.devices.fields.name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isEdit}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <TextField
                  label={t("management.devices.rfid.common.fields.readpointId")}
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <TextField
                  label={t("management.devices.rfid.common.fields.username")}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <TextField
                  label={t("management.devices.rfid.common.fields.password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => void generatePassword()} edge="end">
                          <RefreshIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={3} alignItems="center" sx={{ minHeight: 56 }}>
                  <FormControlLabel
                    control={
                      <Switch checked={debug} onChange={(_, checked) => setDebug(checked)} />
                    }
                    label={t("management.devices.rfid.common.fields.debug")}
                    sx={{ m: 0 }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableDeduplication}
                        onChange={(_, checked) => setEnableDeduplication(checked)}
                      />
                    }
                    label={t("management.devices.rfid.common.fields.enableDeduplication")}
                    sx={{ m: 0 }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </AdminSectionPanel>

          <AdminSectionPanel
            title={t("management.devices.rfid.common.text.portConfiguration")}
            icon={<PortIcon fontSize="small" />}
          >
            <Stack spacing={1.5}>
              {ports.map((port, index) => (
                <Box
                  key={port.port}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "4rem minmax(0, 1fr) minmax(0, 1fr) auto",
                    },
                    gap: 1.5,
                    alignItems: "start",
                    py: 0.5,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ pt: 2, whiteSpace: "nowrap" }}>
                    {t("management.devices.rfid.common.text.port", {
                      portPort: formatInteger(port.port, i18n.language),
                    })}
                  </Typography>
                  <MuiAutocompleteMultiSelect
                    label={t("management.devices.rfid.readerAdd.fields.fittingRoom")}
                    options={changingRooms}
                    value={changingRooms.filter((room) => room.roomId === port.changingRoomId)}
                    onChange={(next) =>
                      handlePortChange(index, "changingRoomId", next[0]?.roomId ?? 0)
                    }
                    getOptionLabel={(room) =>
                      t("management.devices.rfid.common.text.changingRoomOption", {
                        roomName: room.roomName,
                        roomId: room.roomId,
                      })
                    }
                    getOptionKey={(room) => room.roomId}
                    getOptionSecondary={(room) => room.groupName}
                    loading={isLoadingChangingRooms}
                    disabled={!isEdit && !selectedStore}
                    error={validationErrors[`changingRoom${port.port}`]}
                    helperText={
                      validationErrors[`changingRoom${port.port}`]
                        ? t("management.devices.rfid.common.helperText.selectChangingRoom")
                        : undefined
                    }
                  />
                  <TextField
                    label={t("management.devices.rfid.common.fields.rssiFilter")}
                    type="number"
                    value={port.rssiFilter}
                    onChange={(e) => handlePortChange(index, "rssiFilter", Number(e.target.value))}
                    fullWidth
                  />
                  <Box
                    aria-hidden="true"
                    sx={{
                      alignSelf: "center",
                      color: port.changingRoomId ? "success.main" : "action.disabled",
                      display: "flex",
                      justifySelf: "end",
                    }}
                  >
                    <PortIcon />
                  </Box>
                </Box>
              ))}
            </Stack>
          </AdminSectionPanel>
        </Stack>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button
          type="submit"
          form="rfid-reader-add-form"
          variant="contained"
          size="large"
          startIcon={isEdit ? <SaveIcon /> : <AddIcon />}
        >
          {isEdit
            ? t("management.devices.rfid.readerEdit.actions.saveChanges")
            : t("management.devices.rfid.readerAdd.actions.addReader")}
        </Button>
      </Box>
    </PageShell>
  );
};

export default RFIDReaderAdd;
